import { useState } from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  RedAsterix,
  VStack,
  Button,
  HStack,
  Text,
} from '@wegroup/design-system';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import cogoToast from 'cogo-toast';
import { usePostAnvaSendOfferEmail } from '@flows/src/api/anva-flows/postAnvaSendOfferEmail';
import {
  SendOfferMailBySessionIdBody,
  SendOfferToAnvaBySessionIdResponse,
} from 'wg-api-sdk';
import ActionLinkToast from '@common/src/components/ActionLinkToast';

type EmailOfferFormValues = {
  email: string;
};

const schema = object({
  email: string().email('INVALID_EMAIL').required('REQUIRED'),
});

interface Props {
  sessionId: string;
  documents: SendOfferToAnvaBySessionIdResponse | undefined;
}

const AnvaEmailOfferAccordion = ({ sessionId, documents }: Props) => {
  const { t } = useTranslation();
  const [link, setLink] = useState<string | null>();
  const methods = useForm<EmailOfferFormValues>({
    resolver: yupResolver(schema),
  });

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = methods;

  const { mutateAsync: sendOfferEmail } = usePostAnvaSendOfferEmail();

  const documentsBody: SendOfferMailBySessionIdBody['documents'] = documents
    ? [
        ...(documents.insurance_policies?.flatMap((policy) =>
          policy.documents.map((doc) => ({
            name: doc.filename,
            link: doc.link,
          })),
        ) ?? []),
        ...(documents.policy_packages?.flatMap((pkg) =>
          pkg.documents.map((doc) => ({
            name: doc.filename,
            link: doc.link,
          })),
        ) ?? []),
      ]
    : [];

  const handleSend = handleSubmit(async (data) => {
    if (!sessionId) {
      cogoToast.error(t('YELLOWHIVE_EMAIL_OFFER_MISSING_IDS'));
      return;
    }

    try {
      const response = await sendOfferEmail({
        sessionId,
        payload: {
          email: data.email,
          documents: documentsBody,
        },
      });
      setLink(response.link);
    } catch (e) {
      console.error(e);
      cogoToast.error(t('YELLOWHIVE_EMAIL_OFFER_FAILED'));
    }
  });

  const content = link ? (
    <ActionLinkToast
      actionUrl={link}
      onClose={() => setLink(null)}
      title={t('ANVA_EMAIL_OFFER_SENT')}
      description={t('ANVA_EMAIL_OFFER_LINK_DESCRIPTION')}
      copyButtonText={t('PARTIES_DETAIL_TAB_MIJN_OMGEVING_COPY_LINK')}
    />
  ) : (
    <HStack spacing="4" alignItems="flex-end">
      <FormControl isInvalid={!!errors.email}>
        <FormLabel>
          {t('EMAIL')} <RedAsterix />
        </FormLabel>
        <Input type="email" {...register('email')} />
        {errors.email?.message && (
          <FormErrorMessage>{t(errors.email.message)}</FormErrorMessage>
        )}
      </FormControl>
      <Button onClick={handleSend} isLoading={isSubmitting}>
        {t('SEND')}
      </Button>
    </HStack>
  );

  if (!documentsBody.length) {
    return null;
  }

  return (
    <VStack spacing="4" align="stretch">
      <Text textStyle="h6Headline">{t('ANVA_EMAIL_OFFER_TITLE')}</Text>
      {content}
    </VStack>
  );
};

export default AnvaEmailOfferAccordion;
